/**
 * The route table.
 *
 * Fifteen pages, per the phase-5 inventory. Ordered so the Classroom-native
 * screens come first and the Canvas-superset ones (Modules, Files, Discussions,
 * Syllabus) and the C-GC original (grade calculator) follow — see
 * brainstorm.md 1.9.
 *
 * URL shape note: course routes live under `/c/:courseId` rather than
 * `/courses/:courseId`, because these URLs get typed and shared on a
 * Chromebook and shorter is genuinely better. Assignment detail nests under
 * `classwork` so the back button lands where the student came from.
 *
 * CODE SPLITTING
 * --------------
 * Every page is loaded lazily. Bundled together they came to ~900 kB, which on
 * school wifi is a real wait before anything appears — and most of it is pages
 * the student will not open in a given session. Split, the first paint needs
 * only the shell plus Home.
 *
 * The `.then(m => ({ default: m.X }))` dance is because pages use named exports,
 * which is worth keeping: a named export is greppable and survives a refactor,
 * whereas a default export silently becomes whatever the file happens to export.
 */
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';

import { AppShell } from '@/components/AppShell';
import { CourseLayout } from '@/components/CourseLayout';
import { PageContainer } from '@/components/PageContainer';
import { LoadingLines } from '@/components/states';

const Home = lazy(() => import('@/pages/Home/Home').then((m) => ({ default: m.Home })));
const Stream = lazy(() => import('@/pages/Stream/Stream').then((m) => ({ default: m.Stream })));
const Todo = lazy(() => import('@/pages/Todo/Todo').then((m) => ({ default: m.Todo })));
const Calendar = lazy(() => import('@/pages/Calendar/Calendar').then((m) => ({ default: m.Calendar })));
const Archived = lazy(() => import('@/pages/Archived/Archived').then((m) => ({ default: m.Archived })));
const Settings = lazy(() => import('@/pages/Settings/Settings').then((m) => ({ default: m.Settings })));

const CourseStream = lazy(() =>
  import('@/pages/CourseStream/CourseStream').then((m) => ({ default: m.CourseStream })),
);
const Classwork = lazy(() =>
  import('@/pages/Classwork/Classwork').then((m) => ({ default: m.Classwork })),
);
const AssignmentDetail = lazy(() =>
  import('@/pages/AssignmentDetail/AssignmentDetail').then((m) => ({ default: m.AssignmentDetail })),
);
const People = lazy(() => import('@/pages/People/People').then((m) => ({ default: m.People })));
const Grades = lazy(() => import('@/pages/Grades/Grades').then((m) => ({ default: m.Grades })));
const GradeCalculator = lazy(() =>
  import('@/pages/GradeCalculator/GradeCalculator').then((m) => ({ default: m.GradeCalculator })),
);
const Modules = lazy(() => import('@/pages/Modules/Modules').then((m) => ({ default: m.Modules })));
const Files = lazy(() => import('@/pages/Files/Files').then((m) => ({ default: m.Files })));
const Discussions = lazy(() =>
  import('@/pages/Discussions/Discussions').then((m) => ({ default: m.Discussions })),
);
const DiscussionDetail = lazy(() =>
  import('@/pages/DiscussionDetail/DiscussionDetail').then((m) => ({ default: m.DiscussionDetail })),
);
const Syllabus = lazy(() => import('@/pages/Syllabus/Syllabus').then((m) => ({ default: m.Syllabus })));
const CourseSettings = lazy(() =>
  import('@/pages/CourseSettings/CourseSettings').then((m) => ({ default: m.CourseSettings })),
);
const NotFound = lazy(() => import('@/pages/NotFound/NotFound').then((m) => ({ default: m.NotFound })));

/**
 * Shown while a page chunk downloads. Deliberately the same skeleton language
 * the pages themselves use, so a slow chunk looks like slow data rather than
 * like a different kind of wait.
 */
function RouteFallback() {
  return (
    <PageContainer>
      <LoadingLines lines={5} />
    </PageContainer>
  );
}

export function AppRoutes() {
  const location = useLocation();

  return (
    // `mode="wait"` would hold the old page while the new one loads, which
    // makes navigation feel slower than it is. Cross-fading is closer to how
    // Classroom moves between screens.
    <AnimatePresence initial={false}>
      <Suspense fallback={<RouteFallback />}>
        <Routes location={location} key={location.pathname}>
          <Route element={<AppShell />}>
            {/* Global pages */}
            <Route index element={<Home />} />
            <Route path="stream" element={<Stream />} />
            <Route path="todo" element={<Todo />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="archived" element={<Archived />} />
            <Route path="settings" element={<Settings />} />

            {/* Per-course pages */}
            <Route path="c/:courseId" element={<CourseLayout />}>
              <Route index element={<CourseStream />} />
              <Route path="classwork" element={<Classwork />} />
              <Route path="classwork/:assignmentId" element={<AssignmentDetail />} />
              <Route path="people" element={<People />} />
              <Route path="grades" element={<Grades />} />
              <Route path="calculator" element={<GradeCalculator />} />
              <Route path="modules" element={<Modules />} />
              <Route path="files" element={<Files />} />
              <Route path="discussions" element={<Discussions />} />
              <Route path="discussions/:topicId" element={<DiscussionDetail />} />
              <Route path="syllabus" element={<Syllabus />} />
              <Route path="settings" element={<CourseSettings />} />
            </Route>

            {/* Canvas-shaped URLs redirect to their C-GC equivalent, so a link
                pasted from Canvas lands somewhere sensible instead of on a 404. */}
            <Route path="courses/:courseId/*" element={<CanvasUrlRedirect />} />

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

function CanvasUrlRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/courses\//, '');
  return <Navigate to={`/c/${rest}`} replace />;
}
